export const AUTHOR = { name: "erth. escapes team", url: "/" };

export const ARTICLES = [
  {
    slug: "private-villa-near-bengaluru-large-group",
    title: "Private villa stays near Bengaluru: what to look for when booking for a large group",
    description:
      "Booking a private villa near Bengaluru for 10 to 14 people? Here is what to check before you confirm: bedrooms and bathrooms, meals, the pool, check-in times, add-ons and the fine print.",
    keywords: ["private villa near Bengaluru", "villa for large group Bengaluru", "group villa stay Bangalore", "villa with pool near Bengaluru"],
    date: "2026-09-12",
    property: "dune-villas",
    cover: "/images/exterior-hero.webp",
    coverAlt: "Two private villas at Area 83 near Bengaluru in evening light",
    quickAnswer:
      "For a large group, the villa that works is the one that keeps everyone under one roof with enough bedrooms and bathrooms, includes meals so nobody is cooking for fourteen, has a private pool rather than a shared one, and is close enough to Bengaluru that the weekend is not spent on the road. Confirm the guest count, check-in time and what is included in writing before you pay.",
    sections: [
      {
        h: "Start with the headcount, not the photos",
        p: [
          "Most villa listings lead with a pool shot. The number that actually decides whether your weekend works is how many people can sleep comfortably, and how many bathrooms they share. A six-bedroom villa with six bathrooms is a very different stay from a six-bedroom villa with two.",
          "Dune Villas at Area 83, for example, are two identical private villas. Each has six bedrooms and six bathrooms and sleeps 12 guests plus 2. Booking both puts 24 plus 4 guests across the two villas, side by side.",
        ],
      },
      {
        h: "Ask what is included in the price",
        p: [
          "A nightly rate on its own tells you very little. The questions that matter: are meals included, and which ones? Is the pool private to your villa? Is parking free? Are there extra charges per head for children or additional guests?",
        ],
        list: [
          "Meals: at Dune Villas, breakfast and dinner are included for the group. Lunch can be added at ₹900 per person.",
          "Pool: each Dune Villa has its own swimming pool, not a shared resort pool.",
          "Parking: free on-site parking is available for all guests.",
          "Add-ons: a safari experience can be added at ₹2,000 per person.",
          "Rate: Dune Villas start from ₹60,000 per villa, per night, with breakfast and dinner.",
        ],
      },
      {
        h: "Check the practical details before the fun ones",
        p: [
          "Check-in and check-out times shape your whole plan. A 3 PM check-in means lunch is best had on the way or added at the villa; an 11 AM check-out means a slow breakfast, not a lazy pool morning. Dune Villas check in at 3 PM and check out at 11 AM.",
          "Ask about the kitchen too. Even with meals included, a full kitchen with an electric kettle, geyser, hair dryer and iron box in the villa makes a group stay noticeably easier.",
        ],
      },
      {
        h: "Distance matters more than it looks",
        p: [
          "For a two-night stay, a long drive eats a large share of your weekend. Villas set just off Bannerghatta Road, like Dune Villas at Area 83, keep the journey short for groups coming from across Bengaluru, which means you arrive with the day still ahead of you.",
        ],
      },
      {
        h: "Read the policies that apply to groups",
        list: [
          "Minimum stay: many properties set a minimum stay that varies by season. Ask at the time of booking.",
          "Age policy: at Area 83, children under 18 must be accompanied by an adult.",
          "Specific rooms: you can usually request specific rooms, subject to availability. Mention preferences when you reserve.",
        ],
      },
    ],
    faqs: [
      {
        q: "How many people can stay in a private villa near Bengaluru?",
        a: "It depends on the villa. Dune Villas at Area 83 each sleep 12 guests plus 2 across six bedrooms, and booking both villas accommodates 24 plus 4 guests.",
      },
      {
        q: "Are meals included in villa stays near Bengaluru?",
        a: "Not always. At Dune Villas, breakfast and dinner are included with every stay, and lunch can be added at ₹900 per person.",
      },
      {
        q: "What is the price of a private villa near Bengaluru for a large group?",
        a: "Dune Villas at Area 83 start from ₹60,000 per villa, per night, including breakfast and dinner for the group.",
      },
    ],
  },
  {
    slug: "plan-12-person-weekend-getaway-near-bengaluru",
    title: "How to plan a 12-person weekend getaway near Bengaluru",
    description:
      "A step-by-step checklist for planning a weekend away near Bengaluru for a group of 12: choosing the stay, splitting costs, sorting meals, timing arrivals and keeping everyone happy.",
    keywords: ["weekend getaway near Bengaluru for group", "12 person villa Bengaluru", "group trip near Bangalore", "friends trip villa Bengaluru"],
    date: "2026-09-12",
    property: "dune-villas",
    cover: "/images/courtyard-pool.webp",
    coverAlt: "The private courtyard pool at a Dune Villa",
    quickAnswer:
      "Pick one person to lead, fix the dates first, choose a stay that sleeps the whole group under one roof with meals included, agree the cost split before booking, and plan for a 3 PM arrival and an 11 AM departure. Keep the itinerary light: one shared dinner and one long lunch is usually enough structure for a good weekend.",
    sections: [
      {
        h: "Step 1: Fix the dates before anything else",
        p: [
          "Group trips fall apart on dates, not budgets. Offer two or three weekend options, set a deadline for replies, and go with the weekend most people can make. Book once the dates are fixed; large private villas are limited and the good weekends go early.",
        ],
      },
      {
        h: "Step 2: Choose a stay that fits twelve without splitting up",
        p: [
          "Twelve people across separate hotel rooms rarely feels like a trip together. A single private villa keeps the group in one place, with shared living spaces for the evenings.",
          "Dune Villas at Area 83 are built for exactly this: each villa has six bedrooms and six bathrooms, sleeps 12 plus 2, and has its own swimming pool. If your group grows, the second identical villa sits right next door.",
        ],
      },
      {
        h: "Step 3: Agree the money before you book",
        list: [
          "Work out the total, including add-ons, and divide it per person up front.",
          "Collect contributions before the booking is confirmed, not after the trip.",
          "Keep a small shared pool for extras such as lunch add-ons or activities.",
          "At Dune Villas the rate is per villa, per night (from ₹60,000 with breakfast and dinner), which makes the per-head split straightforward.",
        ],
      },
      {
        h: "Step 4: Sort meals so nobody is stuck cooking",
        p: [
          "Meals are where group weekends get complicated. Choosing a stay where breakfast and dinner are included removes most of the coordination. At Dune Villas, both are part of every stay, and lunch can be added at ₹900 per person. The full kitchen in each villa is there for coffee, late-night snacks and anything the group wants to make together.",
        ],
      },
      {
        h: "Step 5: Plan around check-in and check-out",
        p: [
          "With a 3 PM check-in and 11 AM check-out, the rhythm of the weekend almost writes itself: arrive in the afternoon, settle in around the pool, share dinner and a late evening; the next day is fully yours; the final morning is breakfast and a relaxed departure.",
        ],
      },
      {
        h: "Step 6: Keep the itinerary light",
        p: [
          "A large group does not need an hour-by-hour plan. One long lunch, one late dinner and unhurried time by the pool tend to do more than a packed schedule. If the group wants an activity, add it as an option rather than an obligation. At Area 83 a safari experience can be added at ₹2,000 per person, and the sports courts and games lounge are there for whoever wants them.",
        ],
      },
      {
        h: "A simple packing note for the organiser",
        list: [
          "Share the villa address and the reservations number with everyone: No. 313, Off Bannerghatta Road, Bilwaradahalli, Shanbhoganahalli, Bengaluru 560083; reservations +91 80463 96067.",
          "Confirm arrival times so the first car does not wait alone.",
          "Villas provide essentials such as an electric kettle, geyser, hair dryer and iron box, so pack lighter than you think.",
        ],
      },
    ],
    faqs: [
      {
        q: "Where can 12 people stay together near Bengaluru?",
        a: "Dune Villas at Area 83, off Bannerghatta Road, are private six-bedroom villas that sleep 12 guests plus 2 each, with a private pool and breakfast and dinner included.",
      },
      {
        q: "How do we split the cost of a villa for a group?",
        a: "Take the total for all nights and add-ons, divide by the number of guests, and collect contributions before confirming the booking. A per-villa, per-night rate makes this easy to calculate.",
      },
      {
        q: "What time can we check in and out at Dune Villas?",
        a: "Check-in is at 3 PM and check-out is at 11 AM.",
      },
    ],
  },
  {
    slug: "dune-villas-area-83-everything-you-need-to-know",
    title: "Dune Villas at Area 83: everything you need to know",
    description:
      "The complete guide to Dune Villas at Area 83 near Bengaluru: two private six-bedroom villas with pools, 12+2 guests each, breakfast and dinner included, from ₹60,000 per villa per night. Check-in, add-ons, amenities, location and FAQs.",
    keywords: ["Dune Villas Area 83", "Area 83 villa stay", "luxury villa Bannerghatta Road", "private pool villa Bengaluru", "Dune Villa Bengaluru price"],
    date: "2026-09-12",
    property: "dune-villas",
    cover: "/images/living-room.webp",
    coverAlt: "The living and dining space inside a Dune Villa",
    quickAnswer:
      "Dune Villas at Area 83 are two identical private villas off Bannerghatta Road, Bengaluru. Each has six bedrooms and six bathrooms, sleeps 12 guests plus 2, has its own swimming pool, and includes breakfast and dinner. Rates start from ₹60,000 per villa, per night. Check-in is 3 PM, check-out 11 AM. Lunch (₹900 per person) and a safari experience (₹2,000 per person) can be added.",
    table: [
      ["Property", "Dune Villas at Area 83 (Dune Villa 1 and Dune Villa 2)"],
      ["Brand", "Part of erth. escapes"],
      ["Location", "No. 313, Off Bannerghatta Road, Bilwaradahalli, Shanbhoganahalli, Bengaluru, Karnataka 560083"],
      ["Villas", "2 identical private villas"],
      ["Bedrooms / bathrooms", "6 bedrooms and 6 bathrooms in each villa"],
      ["Guests", "12 + 2 per villa; 24 + 4 across both"],
      ["Pool", "Private swimming pool in each villa"],
      ["Meals", "Breakfast and dinner included"],
      ["Add-ons", "Lunch ₹900 per person; safari experience ₹2,000 per person"],
      ["Rate", "From ₹60,000 per villa, per night"],
      ["Check-in / check-out", "3 PM / 11 AM"],
      ["Amenities", "Kitchen, air conditioning, free parking, electric kettle, geyser, hair dryer, iron box"],
      ["Reservations", "+91 80463 96067 · WhatsApp +91 88846 08383 · info@area83.in"],
    ],
    sections: [
      {
        h: "What are Dune Villas?",
        p: [
          "Dune Villas are the most premium stay at Area 83, a destination set off Bannerghatta Road on the edge of Bengaluru. There are two villas, Dune Villa 1 and Dune Villa 2, and they are identical: six bedrooms, six bathrooms, generous living and dining spaces, a full kitchen and a private swimming pool in each.",
          "They are designed for groups that want to stay together: families, friend groups, birthdays, reunions and intimate celebrations. Dune Villas are part of erth. escapes, a hospitality brand creating spaces that harmonize with nature.",
        ],
      },
      {
        h: "How many guests can stay?",
        p: [
          "Each villa sleeps 12 guests plus 2 across its six bedrooms. Booking both villas accommodates 24 plus 4 guests, with the two villas sitting side by side.",
        ],
      },
      {
        h: "What is included in the price?",
        list: [
          "Exclusive use of the villa with six bedrooms and six bathrooms",
          "Private swimming pool",
          "Breakfast and dinner for the group",
          "Kitchen, air conditioning and free on-site parking",
          "In-villa essentials: electric kettle, geyser, hair dryer and iron box",
        ],
        p: ["Rates start from ₹60,000 per villa, per night, with breakfast and dinner. Lunch can be added at ₹900 per person and a safari experience at ₹2,000 per person."],
      },
      {
        h: "What is there to do at Area 83?",
        p: [
          "Most of the stay tends to happen around the villa itself: the pool, the courtyard, long meals and late evenings. Within Area 83 there are sports courts, a games lounge with a pool table, and lawns that suit celebrations. A safari experience is available as an add-on.",
        ],
      },
      {
        h: "Where exactly are Dune Villas?",
        p: [
          "Dune Villas are at Area 83, No. 313, Off Bannerghatta Road, Bilwaradahalli, Shanbhoganahalli, Bengaluru, Karnataka 560083. The location keeps the drive short for groups coming from across the city while feeling nothing like it.",
        ],
      },
      {
        h: "How do I book?",
        p: [
          "Enquire through the form on the Dune Villas page, call reservations on +91 80463 96067, or message +91 88846 08383 on WhatsApp. Share your dates, group size and occasion and the team will help you choose the right villa.",
        ],
      },
    ],
    faqs: [
      {
        q: "What is included in a Dune Villas stay?",
        a: "Every Dune Villa stay includes six bedrooms, access to the swimming pool, and complimentary breakfast and dinner for your group. Lunch (₹900 per person) and a safari experience (₹2,000 per person) can be added on.",
      },
      { q: "What are the check-in and check-out times at Dune Villas?", a: "Check-in is at 3 PM and check-out is at 11 AM." },
      {
        q: "How much does a Dune Villa cost per night?",
        a: "Dune Villas start from ₹60,000 per villa, per night, with breakfast and dinner included.",
      },
      {
        q: "Is there a minimum stay at Dune Villas?",
        a: "There is a minimum stay requirement that varies by season. Check with the team when making your reservation.",
      },
      {
        q: "Is parking available at Dune Villas?",
        a: "Yes. Area 83 offers free on-site parking for all guests.",
      },
      {
        q: "Are there age restrictions at Dune Villas?",
        a: "There are no specific age restrictions, but children under the age of 18 must be accompanied by an adult.",
      },
    ],
  },
  {
    slug: "birthday-reunion-private-villa-planning-guide",
    title: "Celebrating a birthday or reunion in a private villa: a planning guide",
    description:
      "Planning a birthday, reunion or intimate celebration near Bengaluru? How to host it in a private villa: guest list, timings, meals, the celebration itself and the morning after.",
    keywords: ["birthday celebration villa Bengaluru", "reunion venue near Bengaluru", "private villa party Bangalore", "celebration villa with pool near Bengaluru"],
    date: "2026-09-12",
    property: "dune-villas",
    cover: "/images/bonfire-corner.webp",
    coverAlt: "A bonfire corner among plants at Dune Villas at night",
    quickAnswer:
      "A private villa turns a celebration into a whole weekend rather than a single evening. Keep the guest list to what the villa sleeps comfortably, let the included dinner be the centrepiece, add lunch for the day after, and choose a property with lawns and outdoor space for the celebration itself. Dune Villas at Area 83 sleep 12+2 per villa, include breakfast and dinner, and have the lawns of Area 83 around them.",
    sections: [
      {
        h: "Why a villa instead of a venue",
        p: [
          "A restaurant or banquet hall gives you a few hours. A private villa gives you the evening before, the celebration itself and a slow morning after, with nobody watching the clock or driving home late. For milestone birthdays and reunions, that extra time is usually the point.",
        ],
      },
      {
        h: "Build the guest list around the beds",
        p: [
          "Decide who is staying over before you decide who is invited. A Dune Villa sleeps 12 guests plus 2 across six bedrooms; with both villas booked, that becomes 24 plus 4. Working backwards from those numbers keeps the celebration comfortable rather than crowded.",
        ],
      },
      {
        h: "Let the meals do the heavy lifting",
        list: [
          "Dinner: included at Dune Villas, so the celebration dinner is already taken care of.",
          "Breakfast: also included, which makes the morning after effortless.",
          "Lunch: add it at ₹900 per person for the day everyone is there.",
          "Kitchen: each villa has a full kitchen for a cake, drinks and anything the group brings.",
        ],
      },
      {
        h: "Plan the celebration space",
        p: [
          "For the celebration itself, look for outdoor space that can hold the whole group: lawns for the evening, a courtyard and pool for the afternoon, a corner for a bonfire once the night gets going. Dune Villas have the lawns and experiences of Area 83 around them, and each villa has its own courtyard and pool.",
        ],
      },
      {
        h: "Timing a celebration weekend",
        list: [
          "Day 1, 3 PM: check in, settle around the pool, dinner together.",
          "Day 2: a long lunch (add-on), the celebration in the evening, dinner included.",
          "Day 3, 11 AM: breakfast and check-out. Ask about a minimum stay for your dates when booking.",
        ],
      },
      {
        h: "Small details that make a difference",
        list: [
          "Share the address and reservations number with guests in advance: No. 313, Off Bannerghatta Road, Bengaluru 560083; +91 80463 96067.",
          "Mention the occasion when you enquire so the team can help plan around it.",
          "Children under 18 must be accompanied by an adult at Area 83.",
          "Free on-site parking means guests driving in for the evening have somewhere to leave the car.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can I host a birthday party at Dune Villas?",
        a: "Yes. Dune Villas are designed for celebrations and group stays, with private villas sleeping 12+2 each and the lawns of Area 83 around them. Mention the occasion when you enquire.",
      },
      {
        q: "How many guests can a private villa celebration near Bengaluru hold?",
        a: "At Dune Villas, one villa sleeps 12 guests plus 2; booking both villas accommodates 24 plus 4 guests staying overnight.",
      },
      {
        q: "Is dinner included for a celebration at Dune Villas?",
        a: "Yes. Breakfast and dinner are included with every Dune Villas stay. Lunch can be added at ₹900 per person.",
      },
    ],
  },
  {
    slug: "what-sustainable-stays-mean-at-erth-escapes",
    title: "What sustainable stays mean at erth. escapes",
    description:
      "erth. escapes creates innovative spaces that harmonize with nature. What that means in practice, from eco-conscious design to the way a stay is meant to feel, and how Dune Villas at Area 83 fits in.",
    keywords: ["erth escapes", "erth. escapes", "sustainable villa stay India", "eco-conscious retreat India", "sustainable hospitality brand India"],
    date: "2026-09-12",
    property: null,
    cover: "/images/courtyard-walk.webp",
    coverAlt: "A shaded courtyard walk lined with greenery at an erth. escapes property",
    quickAnswer:
      "erth. escapes is a hospitality brand building spaces that harmonize with nature: eco-conscious residences and immersive retreats designed for living, celebrating and reconnecting. Its first project is Dune Villas at Area 83 near Bengaluru, two private six-bedroom villas set among courtyards, palms and pools.",
    sections: [
      {
        h: "Who is erth. escapes?",
        p: [
          "Welcome to Erth Escapes, where we create innovative spaces that harmonize with nature. Our commitment to sustainable living and modern design results in homes and experiences that inspire and connect. From eco-conscious residences to immersive adventure retreats, Erth Escapes brings you thoughtfully crafted environments that redefine living, celebrating, and reconnecting with the people around you and the world around us.",
          "That is the brand in its own words. The rest of this piece is about what it means when you actually arrive.",
        ],
      },
      {
        h: "Design that works with the landscape",
        p: [
          "The starting point is the site, not the building. Courtyards that open onto greenery, glass walls that bring palms into the living room, walkways shaded by planting and pools tucked into private courtyards: the intent is that the architecture frames nature rather than replacing it.",
        ],
      },
      {
        h: "Three words: living, celebrating, reconnecting",
        list: [
          "Living: homes and stays shaped around light, air and green, designed for everyday comfort rather than spectacle.",
          "Celebrating: spaces generous enough for the whole group, so the occasions that matter happen together.",
          "Reconnecting: long lunches, slow mornings and time with the people around you, away from the pace of the city.",
        ],
      },
      {
        h: "Where it begins: Dune Villas at Area 83",
        p: [
          "The first erth. escapes project is Dune Villas at Area 83, off Bannerghatta Road in Bengaluru. Two identical private villas, each with six bedrooms, six bathrooms and a private pool, sleeping 12 guests plus 2, with breakfast and dinner included. They are made for families, friend groups and celebrations that deserve room to unfold.",
        ],
      },
      {
        h: "What comes next",
        p: [
          "erth. escapes is building a collection of escapes across India. New projects will be added to this site as they are announced. For enquiries about Dune Villas or the brand, reach the team at info@area83.in or +91 80463 96067.",
        ],
      },
    ],
    faqs: [
      {
        q: "What is erth. escapes?",
        a: "erth. escapes is a hospitality brand that creates innovative spaces harmonizing with nature, from eco-conscious residences to immersive adventure retreats, designed for living, celebrating and reconnecting.",
      },
      {
        q: "Which properties are part of erth. escapes?",
        a: "The first erth. escapes project is Dune Villas at Area 83 near Bengaluru: two private six-bedroom villas with pools, sleeping 12+2 guests each. Further escapes across India will be announced on this site.",
      },
      {
        q: "How do I contact erth. escapes?",
        a: "Email info@area83.in, call +91 80463 96067, or message +91 88846 08383 on WhatsApp.",
      },
    ],
  },
];

export const getArticle = (slug) => ARTICLES.find((a) => a.slug === slug);

export const readingTime = (a) => {
  const words = [
    a.quickAnswer,
    ...a.sections.flatMap((s) => [s.h, ...(s.p || []), ...(s.list || [])]),
    ...a.faqs.flatMap((f) => [f.q, f.a]),
  ]
    .join(" ")
    .split(/\s+/).length;
  return Math.max(2, Math.round(words / 200));
};

export const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
